import { DndContext, type DragEndEvent, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Box,
  Button,
  Container,
  List,
  ListSubheader,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useState } from 'react';
import { useDataProvider, useGetList, useNotify } from 'react-admin';
import { Else, If, Then } from 'react-if';

import DraggableAlbumRow from './Components/DraggableAlbumRow';
import { useOrderParams } from './useOrderParams';
import type {
  DiscographyAlbumArtist,
  DiscographyAlbumEng, DiscographyAlbumIdentifierNavigation, Resource,
} from '@/Api/studioServerTypes';
import { getResourceFilter } from '@/constants';

type DiscographyAlbumEngRow = DiscographyAlbumEng & {
  id: string;
  CoverImageUrl?: string | null;
  DiscographyAlbumIdentifierNavigation?: DiscographyAlbumIdentifierNavigation | null;
};

export default function AlbumEngineerOrderPage(): JSX.Element {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { mode, resourceId, setResourceId } = useOrderParams();

  const isSelectedWork = mode === 'selectedWork';

  // ---- ENGINEERS LIST ----
  const { data: engineers = [], isLoading: loadingEngineers } = useGetList<Resource>('resources', {
    sort: { field: 'Name', order: 'ASC' },
    filter: { ...getResourceFilter() },
  });

  // ---- ALBUM / ENGINEER ROWS ----
  const {
    data: serverRows = [],
    isLoading: loadingRows,
    refetch,
  } = useGetList<DiscographyAlbumEngRow>('album/engineers', {
    sort: {
      field: isSelectedWork
        ? 'DisplayOrderSelectedWork'
        : 'DisplayOrderCareerHighlights',
      order: 'ASC',
    },
    filter: resourceId
      ? {
        ResourceIdentifier: resourceId,
        [isSelectedWork ? 'SelectedWork' : 'CareerHighlight']: true,
      }
      : {},
  });

  // Latest order from DnD; if empty, we fall back to serverRows when saving
  const [rowsToSave, setRowsToSave] = useState<DiscographyAlbumEngRow[]>([]);
  const [saving, setSaving] = useState(false);

  const handleSaveClick = async () => {
    const rows = rowsToSave.length ? rowsToSave : serverRows;
    if (!rows.length) return;

    setSaving(true);
    try {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];

        const payload: DiscographyAlbumEng = {
          DiscographyAlbumEngIdentifier: r.DiscographyAlbumEngIdentifier,
          DiscographyAlbumIdentifier: r.DiscographyAlbumIdentifier,
          ResourceIdentifier: r.ResourceIdentifier,
          SubmittedDate: r.SubmittedDate,
          DateAdded: r.DateAdded,
          PostInstagram: !!r.PostInstagram,
          SelectedWork: !!r.SelectedWork,
          CareerHighlight: !!r.CareerHighlight,
          AccountNumber: r.AccountNumber,
          WpId: r.WpId ?? null,
          AddedBy: r.AddedBy,
          ModifiedBy: r.ModifiedBy,
          DateModified: r.DateModified,
          Atmos: !!r.Atmos,
          Vinyl: !!r.Vinyl,
          Stereo: !!r.Stereo,
          AtmosTracks: r.AtmosTracks ?? null,
          StereoTracks: r.StereoTracks ?? null,
          DisplayOrderSelectedWork: r.DisplayOrderSelectedWork ?? null,
          DisplayOrderCareerHighlights: r.DisplayOrderCareerHighlights ?? null,
        };

        if (isSelectedWork) {
          payload.DisplayOrderSelectedWork = i;
        } else {
          payload.DisplayOrderCareerHighlights = i;
        }

        await dataProvider.update('album/engineers', {
          id: r.DiscographyAlbumEngIdentifier,
          data: payload,
          previousData: r,
        });
      }

      await refetch();
      notify('Display order saved', { type: 'success' });
    } catch {
      notify('Failed to save', { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container sx={{ py: 3 }}>
        <Typography
          variant='h5' fontWeight={700}
          sx={{ mb: 1 }}
        >
          {isSelectedWork ? 'Selected Work' : 'Career Highlights'}
        </Typography>

        <Stack
          direction='row' alignItems='center'
          spacing={1} sx={{ mb: 1 }}
        >
          <Autocomplete<Resource, false, false, false>
            loading={loadingEngineers}
            options={engineers}
            value={engineers.find((e) => e.id === resourceId) ?? null}
            getOptionLabel={(o) => o?.Name ?? ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, next) => setResourceId(next?.id ?? '')}
            sx={{ width: 340 }}
            renderInput={(params) => (
              <TextField
                {...params} size='small'
                label='Engineer'
              />
            )}
          />

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={handleSaveClick}
            variant='contained'
            startIcon={(
              <If condition={saving}>
                <Then>
                  <CircularProgress size={20} color='inherit' />
                </Then>
                <Else>
                  <SaveIcon />
                </Else>
              </If>
            )}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>

        <List disablePadding>
          <ListSubheader disableSticky sx={{ px: 0, py: 1 }}>
            <HeaderRow />
          </ListSubheader>

          <If condition={!resourceId || loadingEngineers || loadingRows}>
            <Then>
              <Box sx={{ px: 2, py: 3, color: 'text.secondary' }}>
                {!resourceId ? 'Choose an engineer to view albums.' : 'Loading…'}
              </Box>
            </Then>
            <Else>
              <If condition={serverRows.length === 0}>
                <Then>
                  <Box sx={{ px: 2, py: 3, color: 'text.secondary' }}>
                    No albums for this engineer.
                  </Box>
                </Then>
                <Else>
                  <DnDList
                    key={`${mode}|${resourceId || 'none'}`}
                    initialRows={serverRows}
                    onOrderChange={setRowsToSave}
                  />
                </Else>
              </If>
            </Else>
          </If>
        </List>
      </Container>
    </Box>
  );
}

function HeaderRow(): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        color: 'text.secondary',
        letterSpacing: '.04em',
        py: 1,
      }}
    >
      <Box sx={{ flex: '0 0 64px', px: 2, textAlign: 'center' }}>Order</Box>
      <Box sx={{ flex: '0 0 72px', px: 2, textAlign: 'center' }}>Cover</Box>
      <Box sx={{ flex: 2, px: 2 }}>Album Title</Box>
      <Box sx={{ flex: 2, px: 2 }}>Artist Name</Box>
      <Box sx={{ flex: 1, px: 2, textAlign: 'center' }}>Release</Box>
      <Box sx={{ flex: '0 0 48px', px: 1 }} />
    </Box>
  );
}

function DnDList({ initialRows, onOrderChange }:
{
  initialRows: DiscographyAlbumEngRow[];
  onOrderChange: (rows: DiscographyAlbumEngRow[])=> void;
}): JSX.Element {
  const [rows, setRows] = useState<DiscographyAlbumEngRow[]>(() => initialRows);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const from = rows.findIndex((r) => String(r.id) === activeId);
    const to = rows.findIndex((r) => String(r.id) === overId);
    if (from < 0 || to < 0) return;

    const next = arrayMove(rows, from, to);
    setRows(next);
    onOrderChange(next);
  };

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={rows.map((r) => String(r.id))}
        strategy={verticalListSortingStrategy}
      >
        {rows.map((r, i) => (
          <DraggableAlbumRow
            key={r.id}
            id={r.id}
            position={i + 1}
            cover={r.CoverImageUrl ?? ''}
            title={r.DiscographyAlbumIdentifierNavigation?.Title ?? 'N/A'}
            artistName={
              (r.DiscographyAlbumIdentifierNavigation?.DiscographyAlbumArtists ?? [] as (DiscographyAlbumArtist | null)[])
                .map((a) => a?.DiscographyArtistIdentifierNavigation?.Name)
                .filter(Boolean)
                .join(', ') || 'N/A'
            }
            releaseYear={
              r.DiscographyAlbumIdentifierNavigation?.ReleaseDate
                ? new Date(String(r.DiscographyAlbumIdentifierNavigation.ReleaseDate)).getFullYear()
                : undefined
            }
          />
        ))}

      </SortableContext>
    </DndContext>
  );
}
